<template>
  <div v-if="canRead">
    <div class="row">
      <div class="col-md-6">
        <div class="card">
          <div class="card-body">
            <el-table class="table-striped indexed-table-column-width"
                      @cell-mouse-enter="startBounce"
                      @cell-mouse-leave="stopBounce"
                      :data="pagedData"
                      border
                      style="table-layout:fixed; width:100%;">
              <el-table-column
                :width="indexColumnWidth"
                align="center"
                label="#"
                type="index">
              </el-table-column>
              <el-table-column
                label="Driver"
                min-width="135">
                <template slot-scope="props">
                  <div class="no-wrap-ellipsis">{{ props.row.title }}</div>
                </template>
              </el-table-column>
              <el-table-column
                label="Location"
                min-width="200">
                <template slot-scope="props">
                  <div class="no-wrap-ellipsis">{{ props.row.description }}</div>
                </template>
              </el-table-column>
              <el-table-column
                label="Speed"
                min-width="80">
                <template slot-scope="props">
                  {{ props.row.speed }}
                </template>
              </el-table-column>
            </el-table>
            <div class="col-sm-12 pagination-info">
              <span class="category">Showing {{from + 1}} to {{to}} of {{total}} entries</span>
            </div>
            <div class="col-sm-12">
              <p-pagination class="pull-right"
                            v-on:input="loadMarkers"
                            v-model="pagination.currentPage"
                            :per-page="pagination.perPage"
                            :total="pagination.total">
              </p-pagination>
            </div>
            <div style="clear:both">
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div class="card">
          <form class="form-horizontal">
            <div class="card-body">
              <div ref="inTouchGpsMap" class="map map-big" style="min-height:543px"></div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import {Loader} from 'google-maps'
  import Constants from 'src/utilities/constants'
  import PPagination from 'src/components/UIComponents/Pagination.vue'
  import {formatLatLng, deviceId, setIndexColumnWidth} from 'src/utilities/utils'
  import Permissions from 'src/utilities/permissions'

  export default {
    components: {
      PPagination
    },
    computed: {
      pagedData() {
        return this.tableData.slice(this.from, this.to)
      },
      tableData() {
        return this.$store.getters['inTouchGps/devices']
      },
      to() {
        let highBound = this.from + this.pagination.perPage
        if (this.total < highBound) {
          highBound = this.total
        }
        return highBound
      },
      from() {
        return this.pagination.perPage * (this.pagination.currentPage - 1)
      },
      total() {
        this.pagination.total = this.tableData.length
        return this.tableData.length
      },
      canRead() {
        return this.$can('read', Permissions.IN_TOUCH)
      },
      indexColumnWidth() {
        return setIndexColumnWidth(this.pagination.perPage)
      }
    },
    data() {
      return {
        google: null,
        map: null,
        mapCenter: {lat: Constants.ATS.HQ.COORDS.LAT, lng: Constants.ATS.HQ.COORDS.LNG},
        markers: {},
        markerAnimation: null,
        constants: Constants,
        pagination: {
          perPage: 15,
          currentPage: 1,
          perPageOptions: [15, 30, 45, 60],
          total: 0
        }
      }
    },
    methods: {
      loadMarkers() {
        this.deleteMarkers()
        for (let device of this.pagedData) {
          const markerConfig = {
            map: this.map,
            title: device.title + ': ' + device.description
          }
          let position = formatLatLng(device['georss:point'])

          if (position) {
            markerConfig.position = new this.google.maps.LatLng(position)
            const prop = deviceId(device.vin)
            this.markers[prop] = new this.google.maps.Marker(markerConfig)
          }
        }
      },
      startBounce(device) {
        const prop = deviceId(device.vin)
        if (this.markers.hasOwnProperty(prop)) {
          const marker = this.markers[prop]
          marker.setAnimation(this.markerAnimation)
        }
      },
      stopBounce(device) {
        const prop = deviceId(device.vin)
        if (this.markers.hasOwnProperty(prop)) {
          const marker = this.markers[prop]
          marker.setAnimation(null)
        }
      },
      // Sets the map on all markers in the array
      setMapOnAll(map) {
        for (const prop in this.markers) {
          this.markers[prop].setMap(map)
        }
      },
      // Removes the markers from the map, but keeps them in the array.
      clearMarkers() {
        this.setMapOnAll(null)
      },
      // Deletes all markers in the array by removing references to them.
      deleteMarkers() {
        this.clearMarkers()
        this.markers = {}
      },
      async initGoogleMap() {
        if (!this.$refs.inTouchGpsMap) {
          return
        }

        const apiKey = this.$store.get('config/byName', Constants.CONFIGS.GOOGLE_MAPS_API_KEY)
        const loader = new Loader(apiKey, {})
        const google = await loader.load()
        this.google = google

        const mapOptions = {
          zoom: 8,
          center: new google.maps.LatLng(this.mapCenter),
          scrollwheel: false // disable scroll over the map, annoying when you scroll through page
        }

        this.map = new google.maps.Map(this.$refs.inTouchGpsMap, mapOptions)
        this.markerAnimation = google.maps.Animation.BOUNCE

        for (let device of this.pagedData) {
          const markerConfig = {
            map: this.map,
            title: device.title
          }
          let position = formatLatLng(device['georss:point'])

          if (position) {
            markerConfig.position = new google.maps.LatLng(position)
            const prop = deviceId(device.vin)
            this.markers[prop] = new google.maps.Marker(markerConfig)
          }
        }
      }
    },
    async mounted() {
      const response = await this.$store.dispatch('inTouchGps/fetchGpsDevices')

      if (response.result === Constants.RETURN_CODES.SUCCESS) {
        setTimeout(() => {
          this.initGoogleMap()
        }, Constants.TIME.MILLIS.ONE_SECOND)
      }
    }
  }
</script>

<style scoped>
  .no-wrap-ellipsis {
    width: 0;
    min-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
</style>
